import cx from "classnames";
import { Location, LocationDescriptorObject as To } from "history";
import { stringify } from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CellMeasurer, CellMeasurerCache, ListRowProps } from "react-virtualized";

import GridItem, { GridItemPlaceholder } from "@/mk2/apps/bazaar/components/GridItem";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarGridUrl } from "@/mk2/apps/bazaar/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Left, LineHeader } from "@/mk2/components/LineHeader";
import { Link } from "@/mk2/components/Link";
import { TrackClick } from "@/mk2/components/TrackClick";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BZ2_NO_RELATED_ITEMS_FOUND,
    HOME_SHOW_MORE_POSTS,
    RELATED_BAZAAR_PRODUCTS,
} from "@/mk/autogenerated/translation";
import { dict } from "@/mk/bazaar/product/categories";
import {
    filterByCategoryAndProps,
    serializeFilterForPathname,
    serializeFilterForQuery,
} from "@/mk/bazaar/product/filter/filterUtils";

import styles from "./RelatedProducts.mscss";

const DEFAULT_ROW_HEIGHT = 397; // Based on desktop

interface StaticProductGridProps {
    products: ProductEntity[];
    parentProduct?: ProductEntity;
    startRowIndex: number;
    slowScroll?: boolean;
}

const StaticProductGrid: React.FC<StaticProductGridProps> = ({
    products,
    parentProduct,
    startRowIndex,
    slowScroll,
}) => {
    const rows: ProductEntity[][] = [];
    for (let i = 0; i < products.length; i += 2) {
        rows.push(products.slice(i, i + 2));
    }

    return (
        <>
            {rows.map((rowProducts, rowIdx) => (
                <div
                    key={startRowIndex + rowIdx}
                    className={cx(
                        styles.RelatedProducts__row,
                        slowScroll && styles["RelatedProducts__row--slowScroll"],
                    )}
                >
                    {rowProducts.map((prod, colIdx) => (
                        <TrackClick
                            key={prod.id}
                            name="related_product_click"
                            props={{
                                product_id: parentProduct?.id,
                                related_product_id: prod.id,
                                row: startRowIndex + rowIdx,
                                column: colIdx,
                            }}
                        >
                            <GridItem
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${colIdx % 4}`],
                                )}
                                showCreatedTime={false}
                                showStateBadges={false}
                                product={prod}
                            />
                        </TrackClick>
                    ))}
                </div>
            ))}
        </>
    );
};

interface OwnProps {
    className?: string;
    relatedProducts: ProductEntity[];
    product?: ProductEntity;
    hasNext: boolean;
    loadingState: LoadingState;
    isMobile: boolean;
    location: Location;
    slowScroll?: boolean;

    onLoad(start: number);
}

type Props = OwnProps;

const RelatedProducts = React.memo<Props>(
    ({
        className,
        relatedProducts,
        product,
        loadingState,
        hasNext,
        isMobile,
        location,
        slowScroll,
        onLoad,
    }) => {
        let gridCategoryUrl: To = null;
        if (product) {
            const categoryProperty = product.properties.find((p) => p[0] === "cat");
            const categoryName = categoryProperty[1];
            const categoryDefinition = dict()[categoryName];

            const importantProps =
                categoryDefinition !== undefined && categoryDefinition.pathnameFilters !== undefined
                    ? categoryDefinition.pathnameFilters
                    : [];

            const props = importantProps.reduce((acc, cur) => {
                const prop = product.properties.filter((p) => p[0] === cur);
                if (prop) {
                    acc[cur] = prop.map((p) => p[1]);
                }
                return acc;
            }, {});
            const catFilter = filterByCategoryAndProps(categoryName, props);
            gridCategoryUrl = {
                pathname: url(bazaarGridUrl, {
                    category: categoryDefinition && categoryDefinition.slug,
                    filters: serializeFilterForPathname(catFilter),
                }),
                search: stringify(serializeFilterForQuery(catFilter)),
            };
        }

        const [client, setClient] = useState<boolean>(false);

        const cacheRef = useRef<CellMeasurerCache>(
            new CellMeasurerCache({
                defaultHeight: DEFAULT_ROW_HEIGHT,
                fixedWidth: true,
            }),
        );

        const onLoadCallback = useCallback(
            () => onLoad(relatedProducts.length),
            [onLoad, relatedProducts],
        );

        useEffect(() => {
            setClient(true);
        }, [setClient]);

        // --- Desktop: original InfiniteListLoadable (no ad rows) ---
        const rowItems = isMobile ? 2 : 4;
        const realRowsCount =
            Math.floor(relatedProducts.length / rowItems) +
            (relatedProducts.length % rowItems === 0 ? 0 : 1);
        const rowCount = hasNext ? realRowsCount + 1 : realRowsCount;

        const isRowLoaded = useCallback(
            ({ index }) => {
                return !hasNext || (index + 1) * rowItems < relatedProducts.length;
            },
            [hasNext, rowItems, relatedProducts],
        );

        const renderRow = useCallback(
            (listRowProps: ListRowProps) => {
                const { index, key, style, parent } = listRowProps;

                const rowGroups = relatedProducts
                    .slice(index * rowItems, (index + 1) * rowItems)
                    .map((prod, i) => (
                        <TrackClick
                            key={prod.id}
                            name="related_product_click"
                            props={{
                                product_id: product?.id,
                                related_product_id: prod.id,
                                row: index,
                                column: i,
                            }}
                        >
                            <GridItem
                                key={prod.id}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${i % 4}`],
                                )}
                                showCreatedTime={false}
                                showStateBadges={false}
                                product={prod}
                            />
                        </TrackClick>
                    ));

                while (rowGroups.length < rowItems) {
                    rowGroups.push(
                        !isRowLoaded({ index }) ? (
                            <GridItemPlaceholder
                                key={`placeholder-${rowGroups.length}`}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${rowGroups.length % 4}`],
                                )}
                            />
                        ) : (
                            <div
                                key={`empty-${rowGroups.length}`}
                                className={cx(
                                    styles.RelatedProducts__item,
                                    styles[`RelatedProducts__item--col${rowGroups.length % 4}`],
                                )}
                            />
                        ),
                    );
                }

                return (
                    <CellMeasurer
                        key={key}
                        cache={cacheRef.current}
                        parent={parent}
                        columnIndex={0}
                        rowIndex={index}
                    >
                        <div
                            style={{
                                ...style,
                                ...(!client ? { position: undefined } : null),
                            }}
                        >
                            <div
                                className={cx(
                                    styles.RelatedProducts__row,
                                    slowScroll && styles["RelatedProducts__row--slowScroll"],
                                )}
                            >
                                {rowGroups}
                            </div>
                        </div>
                    </CellMeasurer>
                );
            },
            [relatedProducts, product, rowItems, isRowLoaded, client, slowScroll],
        );

        const renderMobileContent = () => {
            const totalProducts = relatedProducts.length;
            const rel1 = relatedProducts.slice(0, 10);
            const rel2 = relatedProducts.slice(10, 20);
            const rel3 = relatedProducts.slice(20);

            return (
                <>
                    <StaticProductGrid
                        products={rel1}
                        parentProduct={product}
                        startRowIndex={0}
                        slowScroll={slowScroll}
                    />
                    {totalProducts >= 10 && (
                        <AdSlot
                            zone="Market Item - Other 01 - Mobile"
                            location={location}
                            marginTopWithAd={20}
                            marginBottomWithAd={20}
                            lazyLoad
                        />
                    )}
                    {totalProducts > 10 && (
                        <StaticProductGrid
                            products={rel2}
                            parentProduct={product}
                            startRowIndex={5}
                            slowScroll={slowScroll}
                        />
                    )}
                    {totalProducts >= 20 && (
                        <AdSlot
                            zone="Market Item - Other 02 - Mobile"
                            location={location}
                            marginTopWithAd={20}
                            marginBottomWithAd={20}
                            lazyLoad
                        />
                    )}
                    {rel3.length > 0 && (
                        <StaticProductGrid
                            products={rel3}
                            parentProduct={product}
                            startRowIndex={10}
                            slowScroll={slowScroll}
                        />
                    )}
                    {hasNext && (
                        <div className={styles.RelatedProducts__loadMore}>
                            <Btn
                                type={BtnType.BlueOutline}
                                label={HOME_SHOW_MORE_POSTS}
                                onClick={onLoadCallback}
                                disabled={loadingState === LoadingState.LOADING}
                            />
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className={cx(styles.RelatedProducts, className)}>
                <LineHeader>
                    <Left>
                        <h2 className={styles.RelatedProducts__title}>
                            {gridCategoryUrl ? (
                                <Link to={gridCategoryUrl}>{RELATED_BAZAAR_PRODUCTS}</Link>
                            ) : (
                                RELATED_BAZAAR_PRODUCTS
                            )}
                        </h2>
                    </Left>
                </LineHeader>

                <div
                    className={cx(
                        styles.RelatedProducts__items,
                        relatedProducts.length === 0 && styles["RelatedProducts__items--empty"],
                    )}
                >
                    {relatedProducts.length > 0 ? (
                        isMobile ? (
                            renderMobileContent()
                        ) : (
                            <InfiniteListLoadable
                                isRowLoaded={isRowLoaded}
                                loadMoreRows={onLoadCallback}
                                rowHeight={cacheRef.current.rowHeight}
                                rowCount={rowCount}
                                rowRenderer={renderRow}
                                estimatedRowHeight={DEFAULT_ROW_HEIGHT}
                                deferredMeasurementCache={cacheRef.current}
                                numberOfItemsOnFirstPage={2}
                            />
                        )
                    ) : (
                        BZ2_NO_RELATED_ITEMS_FOUND
                    )}
                </div>
            </div>
        );
    },
);

RelatedProducts.displayName = "RelatedProducts";

export default RelatedProducts;

interface PlaceholderOwnProps {
    className?: string;
}

export const RelatedProductsPlaceholder = React.memo<PlaceholderOwnProps>(({ className }) => (
    <div className={cx(styles.RelatedProducts, className)}>
        <LineHeader>
            <Left>
                <h2 className={styles.RelatedProducts__title}>{RELATED_BAZAAR_PRODUCTS}</h2>
            </Left>
        </LineHeader>

        <div className={cx(styles.RelatedProducts__items)}>
            {Array.from(Array(8).keys()).map((index) => (
                <GridItemPlaceholder
                    key={index}
                    className={cx(
                        styles.RelatedProducts__item,
                        styles[`RelatedProducts__item--col${index % 4}`],
                    )}
                />
            ))}
        </div>
    </div>
));
